import { useState } from "react";
import { Search } from "lucide-react";
import { useProto } from "../ProtoContext";
import { PEOPLE, MY_PEOPLE, ALL_PEOPLE, leaderName } from "../data";
import { Card, Eyebrow, PersonLine, lastText, nextText } from "../ui";
import { RhythmAvatar } from "../Rhythm";
import { RequestPerson } from "../RequestPerson";
import { cn } from "@/lib/utils";

const RANK = { past: 0, today: 1, rhythm: 2, later: 3 };

const Row = ({ p, showCaregiver }) => {
  const { openPerson } = useProto();
  return (
    <PersonLine
      p={p}
      testid={`person-row-${p.id}`}
      onOpen={() => openPerson(p.id)}
      why={p.reason}
      last={lastText(p)}
      next={{ text: nextText(p), tone: p.status === "paused" ? "later" : p.next.tone }}
      owner={leaderName(p.caregiver)}
      showOwner={showCaregiver}
      avatar={<RhythmAvatar person={p} size={48} />}
    />
  );
};

const Group = ({ status: key, ids, showCaregiver }) => {
  const { status } = useProto();
  if (!ids.length) return null;
  const s = status[key];
  return (
    <section className="mt-12" data-testid={`people-group-${key}`}>
      <div className="px-3">
        <div className="flex items-baseline gap-2"><h2 className="t-heading">{s.label}</h2><span className="t-meta">{ids.length}</span></div>
        <p className="t-meta mt-0.5">{s.hint}</p>
      </div>
      <div className="mt-3 px-3 rule-list border-t border-line">{ids.map((id) => <Row key={id} p={PEOPLE[id]} showCaregiver={showCaregiver} />)}</div>
    </section>
  );
};

export default function People() {
  const { role, isPhone, status } = useProto();
  const filters = [{ id: "all", label: "Everyone" }, { id: "active", label: status.active.label }, { id: "rhythm", label: status.rhythm.label }];
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const pastor = role === "pastor";
  const ids = (pastor ? ALL_PEOPLE : MY_PEOPLE).filter((id) => {
    const p = PEOPLE[id];
    const hit = `${p.first} ${p.last} ${p.household.members.map((m) => m.name).join(" ")}`.toLowerCase().includes(q.toLowerCase());
    return hit && (filter === "all" || p.status === filter);
  });
  const by = (s) => ids.filter((id) => PEOPLE[id].status === s).sort((a, b) => RANK[PEOPLE[a].next.tone] - RANK[PEOPLE[b].next.tone]);
  return (
    <div className={cn("pb-36", isPhone ? "px-3 pt-8" : "max-w-3xl mx-auto px-8 pt-16")} data-testid="people-screen">
      <div className="px-3">
        <Eyebrow>{pastor ? "Riverside Community Church" : "12 people in your care"}</Eyebrow>
        <h1 className="t-greeting mt-3">{pastor ? "People" : "My People"}</h1>
        <label className="field mt-7 flex items-center gap-3 h-14 px-4">
          <Search size={20} className="text-ink-faint" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or family" className="flex-1 bg-transparent outline-none text-[17px] placeholder:text-ink-faint" data-testid="people-search-input" />
        </label>
        <div className="flex gap-1 mt-4 overflow-x-auto no-scrollbar -mx-1 px-1">
          {filters.map((f) => (
            <button key={f.id} onClick={() => setFilter(f.id)} aria-pressed={filter === f.id} className="filter" data-testid={`people-filter-${f.id}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>
      <Group status="active" ids={by("active")} showCaregiver={pastor} />
      <Group status="rhythm" ids={by("rhythm")} showCaregiver={pastor} />
      <Group status="paused" ids={by("paused")} showCaregiver={pastor} />
      {!ids.length && <p className="px-3 mt-10 text-ink-soft" data-testid="people-empty">No one in your care matches “{q}”.</p>}
      <div className="px-3"><RequestPerson initial={ids.length ? "" : q} key={q && !ids.length ? q : "r"} /></div>
    </div>
  );
}
